<template>
    <div class="home">
        <b-container fluid>
          <div id="pgTitle" tabindex="-1"><h1>{{pageTitle}}</h1></div>
          <b-row>
            <b-col>
              <b-alert variant="danger" dismissible :show="(!this.hasProduct) && isSubmitted">
                Cannot make an empty order.
              </b-alert>
            </b-col>
          </b-row>

          <form-wizard :stepSize="stepSize"
                       :nextButtonText="buttons.next"
                       :backButtonText="buttons.back"
                       :finishButtonText="hasFee ? 'Proceed to Payment' : 'Submit Order'"
                       color="#003865"
                       @on-complete="onSubmit"
                       @on-change="switchTabs"
                       id="wizard"
                       ref="wizard">
            <span slot="title" tabindex="-1"/>

            <tab-content title="Terms of Service"
                         :before-change="()=>validateStep('tos')"
                         :after-change="()=>scrollTo()"
                         :additional-info="{id: 'tos'}"
            >
              <TermsAcknowledgement ref="tos"
                        @on-validate="submitStep('tos')"
                        @on-submit="loadProfile"
              />
            </tab-content>

            <tab-content title="Attestation"
                          :before-change="()=>validateStep('attestation')"
                          :after-change="()=>scrollTo()"
                          :additional-info="{id: 'attestation'}"
            >
              <Attestation ref="attestation"
                          :products="products"/>
            </tab-content>

            <tab-content title="Notarize"
                         :before-change="()=>validateStep('notarize')"
                         :after-change="()=>scrollTo()"
                         :additional-info="{id: 'notarize'}"
                         v-if="requiresNotary"
            >
              <Notarize ref="notarize"
                         :order="order" :requiresNotary="requiresNotary"
              />
            </tab-content>

            <tab-content title="Documents"
                         :before-change="()=>validateStep('documents')"
                         :after-change="()=>scrollTo()"
                         :additional-info="{id: 'documents'}"
                         v-if="requiresDocuments"
            >
              <Documents ref="documents"
                         :order="order"
                         :requiredAttachmentOptions="requiredAttachmentOptions"
                         :optionalAttachmentOptions="[]"
                         :requiresOneAttachmentOptions="requiresOneAttachmentOptions"
              />
            </tab-content>

            <tab-content title="Customer Information"
                         :before-change="()=>validateStep('customer')"
                         :after-change="()=>scrollTo()"
                         :additional-info="{id: 'customer'}"
            >
              <CustomerInformation ref="customer"
                         @on-validate="mergePartialModels"
                         @on-submit="loadOrder"
                         :order="order" :profile="profile" :products="products" :cityOptions="cityOptions" :countryOptions="countryOptions" :hasDataFile="hasDataFile"/>
            </tab-content>

            <tab-content title="Shipping Information"
                         :before-change="()=>validateStep('shipping')"
                         :after-change="()=>scrollTo()"
                         :additional-info="{id: 'shipping'}"
                         v-if="requiresShipping"
            >
              <ShippingInformation ref="shipping"
                                   @on-validate="mergePartialModels"
                                   @on-submit="loadOrder"
                                   :order="order" :profile="profile" :products="products" :cityOptions="cityOptions"
                                   :countryOptions="countryOptions" :hasDataFile="hasDataFile" :isDataFileOnly="isDataFileOnly"
                                   :hasReplacement="hasReplacement" :flag="flag"
              />
            </tab-content>

            <tab-content title="Amendment Documentation"
                         :before-change="()=>validateStep('amend')"
                         :after-change="()=>scrollTo()"
                         :additional-info="{id: 'amend'}"
                         v-if="requiresAmendmentDocs"
            >
              <AmendmentDocument ref="amend"
                                 :orderNumber="order.orderNumber"
              />
            </tab-content>

            <tab-content title="Order Summary"
                         :before-change="()=>validateStep('summary')"
                         :after-change="()=>scrollTo()"
                         :additional-info="{id: 'summary'}"
            >
              <OrderSummary ref="summary"
                        v-on:load-order="loadOrder"
                        v-on:change-tab="handleTabChange"
                        :order="order" :profile="profile" :attachments="attachments"
                        :requiresNotary="requiresNotary" :requiresDocuments="requiresDocuments"
              />
            </tab-content>

          </form-wizard>
        </b-container>
    </div>
</template>

<script>
import axios from 'axios'
import axiosRetry from 'axios-retry';
import {toastService} from "@/mixins/toastService";
import {buttons} from "@/mixins/constants";
import OrderSummary from "@/components/checkout-wizard/OrderSummary.vue";
import Documents from "@/components/checkout-wizard/Documents.vue";
import Notarize from "@/components/checkout-wizard/Notarize.vue";
import CustomerInformation from "@/components/checkout-wizard/CustomerInformation.vue";
import ShippingInformation from "@/components/checkout-wizard/ShippingInformation.vue";
import LocalStorageService from "@/common/LocalStorageService";
import TermsAcknowledgement from "@/components/checkout-wizard/TermsAcknowledgement.vue";
import Attestation from "@/components/checkout-wizard/Attestation.vue";
import {totalService} from "@/mixins/totalService";
import AmendmentDocument from "@/components/checkout-wizard/AmendmentDocument.vue";

axiosRetry(axios, { retries: 0,
  retryDelay: axiosRetry.exponentialDelay  })

const localStorageService = LocalStorageService.getService();

export default {
  name: "Cart",
  components:{
    AmendmentDocument,
    Attestation,
    TermsAcknowledgement,
    ShippingInformation,
    CustomerInformation,
    Notarize,
    Documents,
    OrderSummary
  },
  mixins: [
    toastService,
    totalService
  ],

  data: () => {
    return {
      orderNumber: null,
      stepSize: 'sm',
      layout: '',
      isSubmitted: false,
      pageTitle: '',
      requiresNotary: true,
      requiresAmendmentDocs: true,
      requiredAttachmentOptions: [],
      requiresOneAttachmentOptions: [],
      order: {},
      profile: {
        name: {
          lastName: null,
          firstName: null,
          middleName: null,
        },
        phone: null,
        email: null,
        dateOfBirth: null,
        orderId: null,
        address: {
          streetAddress: null,
          poBox: null,
          city: null,
          mnCity: null,
          state: null,
          zip: null,
          country: 'US',
          addressType: "CONTACT",
          international: false,
        }
      },
      products: [],
      cityOptions: [],
      attachments: [],
      countryOptions: [],
      requiresDocuments: true,
      requiresShipping: false,
      flag: false,
    }
  },

  computed: {
    hasProduct() {
      if (this.order && this.order?.orderProducts) {
        let nonzero = this.order.orderProducts
            .some((product) => product.quantity > 0.00)
        return this.order.orderProducts.length > 0 && nonzero
      }
      return false
    },
    hasDataFile() {
      return this.products.length > 0 && this.products.filter(p => p.category === "DATA").length > 0
    },
    isDataFileOnly() {
      return this.hasDataFile && this.products.filter(p => p.category !== "DATA").length === 0
    },
    hasAmendment() {
      return this.order?.orderProducts?.length > 0 && this.order?.orderProducts?.filter(op => op.product.category === "CHANGES"
          && op.product.code.includes("AMENDMENT_")
          && (op.product.code !== 'AMENDMENT_DEATH' || op.relationship?.code === 'OTHER')).length > 0
    },
    hasReplacement() {
      return this.products.length > 0 && this.products.filter(p => p.code === "REPLACEMENT_BIRTH").length > 0
    },
    hasShippingItems() {
      return this.products.length > 0 && this.products.filter(p => p.code !== "REPLACEMENT_BIRTH_COURT_ORDER").length > 0
    },
    hasFee() {
      let fee = this.totalFee(this.order)
      return fee > 0
    },
  },

  methods: {
    buttons: function(){
      return buttons;
    },
    onSubmit(){
      this.isSubmitted = true;
      if (this.hasProduct) {
        this.sendToServer();
      } else {
        this.scrollTo()
      }
    },
    sendToServer(){
      axiosRetry(axios, {retries: 0});

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/order/submit`, {id: this.orderNumber})
          .then(response => {
            toastService.showRootToast('Submitted', 'Order Submitted.  ' +
                'Your order will begin processing once payment is received.', 'success', this.$root);
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/payment/pay`, {id: this.orderNumber})
                .then(response=> {
                  if (!response.data) {
                    this.$router.push('/receipt/'+ this.order.orderNumber)
                  } else {
                    this.sendToUSBank(response.data)
                  }
                  localStorageService.clearCartItems()
                  localStorageService.clearOrderNumber()
                  localStorageService.clearIsDraft()
                })
                .catch(err => {
                  toastService.showRootToast("Error", "Unable to contact US Bank" , "danger", this.$root)
                })
          }).catch(error => {
            if (error?.response?.status) {
              localStorageService.setCartItems(0)
              this.$emit('cart-update')
              toastService.showRootToast('Cannot Order', 'Cannot create order - nothing in cart','danger', this.$root);
              this.$router.push('/product-select')
            } else {
              toastService.showRootToast('Error','The application has encountered an issue.', 'danger', this.$root);
            }
      });
    },
    sendToUSBank(formData) {
      var usbForm = document.createElement('form')
      usbForm.method = 'post'
      usbForm.action = process.env.VUE_APP_USBANK_ENDPOINT
      for (var k in formData) {
        var input = document.createElement('input')
        input.setAttribute('name', k)
        input.setAttribute('value', formData[k])
        usbForm.appendChild(input) ;
      }
      document.body.appendChild(usbForm)
      usbForm.submit()
      document.body.removeChild(usbForm) ;
    },
    loadOrder(){
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/order/orderSummary/${this.orderNumber}`)
          .then(response=>{
            this.order = response.data
            this.requiresAmendmentDocs = this.hasAmendment
            this.requiresShipping = this.hasShippingItems
          })
    },
    loadProducts(){
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/order/products/${this.orderNumber}`)
          .then(response=>{
            this.products = response.data
          })
    },
    loadProfile(){
      if (this.$keycloak.authenticated) {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile`)
            .then(response=>{
              if (response.data != null) {
                this.profile = response.data
              }
            })
      } else {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile/${this.orderNumber}`)
            .then(response=>{
              if (response.data) {
                this.profile = response.data
              }
            })
      }
    },
    loadCities(){
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/options/city/known`)
          .then(response=>{
            this.cityOptions = response.data
          })
    },
    loadCountries() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/options/country`)
          .then(response=>{
            this.countryOptions = response.data
          })
    },
    loadAllAttachments() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/current/all/${this.orderNumber}`)
          .then(response => {
            this.attachments = response.data
          })
    },
    loadRequiresNotary() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/affirm/required/${this.orderNumber}`)
          .then(response => {
            this.requiresNotary = response.data
          })
    },
    loadRequiredAttachments() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/required/${this.orderNumber}`)
          .then(response => {
            this.requiredAttachmentOptions = response.data
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/requiresOne/${this.orderNumber}`)
                .then(response => {
                  this.requiresOneAttachmentOptions = response.data
                  this.requiresDocuments = (this.requiredAttachmentOptions != null && this.requiredAttachmentOptions.length > 0) ||
                      (this.requiresOneAttachmentOptions != null && this.requiresOneAttachmentOptions.length > 0)
                })
          })
    },
    validateStep(name){
      var refToValidate = this.$refs[name];
      return refToValidate.validate();
    },
    submitStep(name) {
      var refToValidate = this.$refs[name];
      refToValidate.submit()
    },
    mergePartialModels(model, isValid, step, load = false){
      if(step === "customer") {
        this.loadAllAttachments()
        this.flag = true
      } else {
        this.flag = false
      }
      if(isValid){
        this.submitStep(step)
        this.order = Object.assign({}, this.order, model);
        if(this.order.opts){
          delete this.order.opts;
        }
      }
    },
    switchTabs(prevIdx, nextIdx){
      var title = 'Your Cart';
      let tabId = this.$refs.wizard.tabs[nextIdx]?.additionalInfo?.id;
      switch(tabId){
        case 'tos':
          title = 'Terms of Service';
          break;
        case 'attestation':
          title = 'Attestation';
          break;
        case 'notarize':
          title = 'Notarize';
          break;
        case 'documents':
          title = 'Documents';
          break;
        case 'customer':
          title = 'Customer Information';
          break;
        case 'shipping':
          title = 'Shipping Information';
          break;
        case 'amend':
          title = 'Amendment Documentation';
          break;
        case 'summary':
          title = 'Order Summary';
          break;
        default:
          title = 'Checkout';
      }
      document.title = title + ' - Online Storefront';
      this.pageTitle = document.title;
      document.getElementById('pgTitle').focus();
    },
    removeTabFocus(){
      this.$nextTick(function(){
        var steps = document.querySelectorAll("div[role='tab']");
        steps.forEach(step => {
          step.setAttribute("tabindex", "-1");
        });
        var steps2 = document.querySelectorAll('.wizard-nav-pills>li>a');
        steps2.forEach(step => {
          step.setAttribute("tabindex","-1");
          step.setAttribute("aria-disabled", true);
        })
      });
    },
    scrollTo(){
      let elem = document.getElementById('wizard');
      elem.scrollIntoView();
    },
    handleTabChange(tab) {
      var tabIndex = this.$refs.wizard.tabs.findIndex(t => t.additionalInfo?.id === tab);
      this.$refs.wizard.changeTab(this.$refs.wizard.activeTabIndex, tabIndex)
    }
  },

  created() {
    document.title = "Checkout - Online Storefront"
    this.pageTitle = document.title;
    this.orderNumber = localStorageService.getOrderNumber()
  },

  updated: function(){
    this.removeTabFocus();
  },

  mounted(){
    // TODO: can these be loaded together?
    this.loadOrder()
    this.loadProducts()
    this.loadProfile()
    this.loadRequiredAttachments();
    this.loadRequiresNotary()

    this.loadCities()
    this.loadCountries()
    this.removeTabFocus();
    this.scrollTo();
  }

}
</script>

<style>
</style>
